import useAppStore from '$appStore'
import { CountryLocaleOptions, LocaleOptions } from '$constants'
import { CompanyPreferences } from '$utils'
import { NumberParser } from '../../numberParser'

/**
 * Formats currency values to the expected 2 decimal places and adds a '$' infront
 * @param currency the currency value to be formatted
 * @returns the formatted currency value
 */
export function formatCurrency(
  currencyValue: string | number | null | undefined,
  hideSymbol?: boolean
): string {
  const companyCountry: string = useAppStore
    .getState()
    .activeCompany?.preference(CompanyPreferences.Locale) as string

  const config: LocaleOptions =
    CountryLocaleOptions[companyCountry] || CountryLocaleOptions['default']

  const LocaleAwareParser = new NumberParser(config.locale)

  const numberFormat: Intl.NumberFormat = Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
  })
  let value: string = ''

  if (!currencyValue) {
    value = numberFormat.format(0)
  } else {
    const safeValue =
      typeof currencyValue === 'string'
        ? LocaleAwareParser.parse(currencyValue)
        : currencyValue
    value = numberFormat.format(safeValue)
  }

  if (hideSymbol) {
    // If the number is negative, the parser puts the '-' before the currency symbol.
    // So if its negative we rant to remove the value at index 1
    value = value.startsWith('-')
      ? value.substring(0, 1) + value.substring(2)
      : value.slice(1)
  }

  return value
}

/**
 * Returns the character used as the thousands separator in the current locale.
 * @returns
 */
export function getThousandsSeparator() {
  const companyCountry: string = useAppStore
    .getState()
    .activeCompany?.preference(CompanyPreferences.Locale) as string

  const config: LocaleOptions =
    CountryLocaleOptions[companyCountry] || CountryLocaleOptions['default']

  const LocaleAwareFormatter = new Intl.NumberFormat(config.locale)

  // We format '1000' here, knowing that the thousands separator will be present
  // as the second part of the result
  const [_, thousandsSeparator] = LocaleAwareFormatter.formatToParts(1000).map(
    (p) => p.value
  )

  return thousandsSeparator
}
